import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import AssessmentPopup from '../dashboard/AssessmentPopup';
import { 
  FiArrowLeft, 
  FiPlus, 
  FiSearch, 
  FiLayers, 
  FiFileText, 
  FiTrash2, 
  FiBookOpen, 
  FiChevronLeft, 
  FiChevronRight,
  FiChevronDown,
  FiInbox,
  FiFilePlus,
  FiCloud,
  FiDownload,
  FiCheck,
  FiX,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { testService } from '../../../services/testService';
import toast from 'react-hot-toast';
import { useAssessmentContext } from '../AssessmentContext';
import '../dashboard/assessment.css';
import '../assessment-module-mobile.css';
import PedagogyDropdownMenu from './PedagogyDropdownMenu';
import PedagogyFieldHeader from './PedagogyFieldHeader';
import { AssessmentEditIcon, assessTipProps } from '../AssessmentContext';

const ASSESSMENT_ACTION_ICON_BTN_CLASS =
  'w-9 h-9 rounded-xl flex items-center justify-center border transition-all shadow-sm shrink-0';

const AssessmentConfirmIconButton = ({
  onClick,
  title = 'Confirm',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-red-100 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white ${className}`}
  >
    <FiCheck className="w-4 h-4 shrink-0" strokeWidth={2.5} />
  </button>
);

const AssessmentCancelIconButton = ({
  onClick,
  title = 'Cancel',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-slate-100 bg-slate-50 text-slate-500 hover:bg-slate-200 hover:text-slate-800 ${className}`}
  >
    <FiX className="w-4 h-4 shrink-0" strokeWidth={2.5} />
  </button>
);

const AssessmentEditIconButton = ({
  onClick,
  title = 'Edit',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 ${className}`}
  >
    <AssessmentEditIcon className="w-4 h-4 shrink-0" strokeWidth={2.5} />
  </button>
);

const AssessmentDeleteIconButton = ({
  onClick,
  title = 'Delete',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-rose-100 bg-rose-50 text-rose-500 hover:bg-[#b91c1c] hover:text-white ${className}`}
  >
    <FiTrash2 className="w-4 h-4 shrink-0" strokeWidth={2} />
  </button>
);

const QB_GRID_MCQ = 'grid-cols-[60px_180px_minmax(0,1fr)_340px]';
const QB_GRID_DESC = 'grid-cols-[60px_180px_minmax(0,1fr)_72px]';
const QB_QUESTION_TYPES = ['MCQ', 'DESCRIPTIVE'];
const QB_QUESTION_TYPE_LABELS = { MCQ: 'MCQ', DESCRIPTIVE: 'Descriptive' };

const MCQ_OPTION_LETTERS = ['A', 'B', 'C', 'D'];

const isMcqQuestionType = (type) => String(type ?? '').toUpperCase() === 'MCQ';

const getMcqOptionText = (question, letter) => {
  const upper = String(letter ?? '').toUpperCase();
  const lower = upper.toLowerCase();
  if (!MCQ_OPTION_LETTERS.includes(upper)) return '';
  return String(question?.[`option_${lower}`] ?? question?.[`option_${upper}`] ?? '').trim();
};

const getQuestionTypeDisplayTag = (type) =>
  isMcqQuestionType(type) ? 'MCQ (Multiple Choice)' : 'Descriptive';

const getQuestionMarksLabel = (marks) => {
  const value = Number(marks) || 1;
  return `${value} Mark${value === 1 ? '' : 's'}`;
};

const resolveMcqCorrectLetter = (question) => {
  const raw = String(question?.correct_answer ?? '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase();
  if (MCQ_OPTION_LETTERS.includes(upper)) return upper;

  // Only "A", "A.", "A)" — not full answer text that happens to start with A
  const letterWithPunctuation = upper.match(/^([A-D])[\.\)\:\;\s-]*$/);
  if (letterWithPunctuation) return letterWithPunctuation[1];

  const rawLower = raw.toLowerCase();
  return (
    MCQ_OPTION_LETTERS.find((letter) => {
      const optionText = getMcqOptionText(question, letter).toLowerCase();
      return optionText !== '' && optionText === rawLower;
    }) || ''
  );
};

const isMcqCorrectOption = (question, letter) =>
  resolveMcqCorrectLetter(question) === String(letter ?? '').toUpperCase();

const normalizeMcqQuestionRow = (question) => {
  if (!isMcqQuestionType(question?.type)) return question;
  const resolved = resolveMcqCorrectLetter(question);
  return resolved ? { ...question, correct_answer: resolved } : question;
};

const DEFAULT_PEDAGOGY_OPTIONS = [
  'Algorithm Design',
  'Analytical Thinking',
  'Aptitude',
  'Code Efficiency',
  'Communication Skills',
  'Database Knowledge',
  'Data Interpretation',
  'Debugging',
  'Logical Reasoning',
];

const QB_CSV_HEADERS = [
  'section_name',
  'question',
  'type',
  'option_a',
  'option_b',
  'option_c',
  'option_d',
  'correct_answer',
  'marks',
  'expected_answer',
  'pedagogy',
];

const QB_CSV_INSTRUCTION =
  '# type: MCQ or Descriptive question. MCQ: fill options and correct_answer (A-D). Descriptive: leave options empty; put the model answer in correct_answer (and expected_answer if used).';

const QB_TEMPLATE_COLUMNS_LABEL = QB_CSV_HEADERS.join(', ');

const sanitizeQuestionBankDownloadName = (name) =>
  (name || 'question_template')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .toLowerCase() || 'question_template';

const formatQuestionBankDownloadTimestamp = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
};

const buildQuestionBankTemplateFileName = (bankName) =>
  `${sanitizeQuestionBankDownloadName(bankName)}_${formatQuestionBankDownloadTimestamp()}.csv`;

const normalizeQuestionBankHeaderKey = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const validateQuestionBankHeaderRow = (headerRow) => {
  const trimmed = headerRow.map((cell) => String(cell ?? '').trim());
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') {
    trimmed.pop();
  }

  if (trimmed.length !== QB_CSV_HEADERS.length) {
    return {
      valid: false,
      error: `Invalid column count (${trimmed.length}). Use the downloaded template with exactly these columns in order: ${QB_TEMPLATE_COLUMNS_LABEL}.`,
    };
  }

  const mismatches = [];
  trimmed.forEach((header, index) => {
    const expected = QB_CSV_HEADERS[index];
    if (normalizeQuestionBankHeaderKey(header) !== normalizeQuestionBankHeaderKey(expected)) {
      mismatches.push(`column ${index + 1} must be "${expected}" (found "${header}")`);
    }
  });

  if (mismatches.length > 0) {
    return {
      valid: false,
      error: `Invalid column names. ${mismatches.join('; ')}. Download the template and keep the header row unchanged.`,
    };
  }

  return { valid: true };
};

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const parseQuestionBankCsvLine = (line) => {
  const result = [];
  let curValue = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(curValue.trim());
      curValue = '';
    } else {
      curValue += char;
    }
  }
  result.push(curValue.trim());
  return result;
};

const normalizeQuestionBankCsvType = (typeRaw) => {
  const type = String(typeRaw || '').trim().toLowerCase();
  if (
    type === 'short answer' ||
    type === 'descriptive' ||
    type === 'descriptive question' ||
    type.includes('descriptive')
  ) {
    return 'DESCRIPTIVE';
  }
  return 'MCQ';
};

const rowToQuestionBankItem = (cols, rowIndex) => {
  const cells = QB_CSV_HEADERS.map((_, index) => String(cols[index] ?? '').trim());
  const question = cells[1];
  if (!question) return null;

  const type = normalizeQuestionBankCsvType(cells[2]);
  const expectedAnswer = cells[9];
  const correctAnswer = cells[7] || (type === 'DESCRIPTIVE' ? expectedAnswer : '');

  return {
    id: Date.now() + rowIndex,
    section_name: cells[0] || 'General',
    question,
    type,
    option_a: cells[3],
    option_b: cells[4],
    option_c: cells[5],
    option_d: cells[6],
    correct_answer: correctAnswer,
    marks: cells[8] || (type === 'DESCRIPTIVE' ? '2' : '1'),
    pedagogy: cells[10] || 'General',
  };
};

const parseQuestionBankTableRows = (tableRows) => {
  const dataRows = (tableRows || [])
    .map((row) => (Array.isArray(row) ? row : Object.values(row)).map((cell) => String(cell ?? '').trim()))
    .filter((row) => {
      const first = String(row[0] ?? '').trim();
      return row.some((cell) => cell !== '') && !first.startsWith('#');
    });

  if (dataRows.length === 0) {
    return { success: false, error: 'File is empty. Download the template and add your questions below the header row.' };
  }

  const headerCheck = validateQuestionBankHeaderRow(dataRows[0]);
  if (!headerCheck.valid) {
    return { success: false, error: headerCheck.error };
  }

  const questions = [];
  for (let i = 1; i < dataRows.length; i += 1) {
    const item = rowToQuestionBankItem(dataRows[i], i);
    if (item) questions.push(item);
  }

  if (questions.length === 0) {
    return {
      success: false,
      error: 'No valid question rows found. Each row needs a value in the "question" column.',
    };
  }

  return { success: true, questions };
};

const parseQuestionBankCsv = (text) => {
  const lines = String(text || '').split(/\r?\n/);
  const dataLines = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    dataLines.push(line);
  });

  const tableRows = dataLines.map((line) => parseQuestionBankCsvLine(line));
  return parseQuestionBankTableRows(tableRows);
};

const parseQuestionBankExcel = (arrayBuffer) => {
  try {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      return { success: false, error: 'Excel file has no worksheets.' };
    }

    const sheet = workbook.Sheets[sheetName];
    const tableRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
    return parseQuestionBankTableRows(tableRows);
  } catch (err) {
    console.error(err);
    return { success: false, error: 'Could not read the Excel file. Save as .csv from the template or upload a valid .xlsx file.' };
  }
};

const mapBankQuestionsFromRaw = (rawQuestions = []) =>
  (rawQuestions || []).map((q) =>
    normalizeMcqQuestionRow({
      id: q.id,
      section_name: q.category,
      question: q.question,
      type: q.type,
      option_a: q.option_a,
      option_b: q.option_b,
      option_c: q.option_c,
      option_d: q.option_d,
      correct_answer: q.correct_answer,
      marks: q.marks,
      pedagogy: q.pedagogy,
      status: q.status || 'Active',
    })
  );

const mapBankListItem = (bank) => ({
  id: bank.id,
  name: bank.name,
  questions: bank.question_count ?? (Array.isArray(bank.questions) ? bank.questions.length : 0),
  sections: bank.section_count ?? 0,
  rawQuestions: Array.isArray(bank.questions) ? bank.questions.map((q) => ({ ...q, status: q.status || 'Active' })) : null,
});

const areQuestionsEqual = (a, b) => {
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  return a.every((q, idx) => {
    const o = b[idx];
    return o &&
      String(q.id) === String(o.id) &&
      (q.status || 'Active') === (o.status || 'Active') &&
      q.question === o.question &&
      q.type === o.type &&
      q.pedagogy === o.pedagogy &&
      Number(q.marks) === Number(o.marks) &&
      q.option_a === o.option_a &&
      q.option_b === o.option_b &&
      q.option_c === o.option_c &&
      q.option_d === o.option_d &&
      q.correct_answer === o.correct_answer;
  });
};

const mergeQuestions = (localQuestions, incomingQuestions, editingId, originalQuestionsCache = []) => {
  const merged = incomingQuestions.map(incomingQ => {
    if (editingId && String(incomingQ.id) === String(editingId)) {
      const localQ = localQuestions.find(l => String(l.id) === String(editingId));
      if (localQ) return localQ;
    }

    const localQ = localQuestions.find(l => String(l.id) === String(incomingQ.id));
    if (localQ) {
      const originalQ = originalQuestionsCache.find(o => String(o.id) === String(incomingQ.id));
      if (originalQ) {
        const isModifiedLocally = 
          localQ.question !== originalQ.question ||
          localQ.type !== originalQ.type ||
          localQ.pedagogy !== originalQ.pedagogy ||
          Number(localQ.marks) !== Number(originalQ.marks) ||
          localQ.option_a !== originalQ.option_a ||
          localQ.option_b !== originalQ.option_b ||
          localQ.option_c !== originalQ.option_c ||
          localQ.option_d !== originalQ.option_d ||
          localQ.correct_answer !== originalQ.correct_answer ||
          (localQ.status || 'Active') !== (originalQ.status || 'Active');

        if (isModifiedLocally) {
          return localQ;
        }
      }
    }
    return incomingQ;
  });

  const tempQuestions = localQuestions.filter(l => !l.id || String(l.id).startsWith('temp'));
  return [...merged, ...tempQuestions];
};

const QuestionBankManager = ({
  onBack, 
  returnToTemplates = false,
  isWizardMode = false,
  selectedQBId = null,
  onSelectQB = null,
  draftQuestionBank = null
}) => {
  const { returnFromModule } = useAssessmentContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [bankName, setBankName] = useState('');
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [newQuestionType, setNewQuestionType] = useState('MCQ');
  const [pedagogySearch, setPedagogySearch] = useState('');
  const [pedagogyOptions, setPedagogyOptions] = useState(DEFAULT_PEDAGOGY_OPTIONS);
  const [savingPedagogy, setSavingPedagogy] = useState(false);
  const [pedagogyDeleteMode, setPedagogyDeleteMode] = useState(false);
  const [selectedPedagogiesForDelete, setSelectedPedagogiesForDelete] = useState([]);
  const [deletingPedagogies, setDeletingPedagogies] = useState(false);
  const [showPedagogyDropdown, setShowPedagogyDropdown] = useState(false);
  const [correctOption, setCorrectOption] = useState(null);
  const [banks, setBanks] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [newOptions, setNewOptions] = useState({ A: '', B: '', C: '', D: '' });
  const [newMarks, setNewMarks] = useState(1);
  const [newCorrectAnswer, setNewCorrectAnswer] = useState('');
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingBankId, setEditingBankId] = useState(null);
  const [editBankName, setEditBankName] = useState('');
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });
  const [popupInput, setPopupInput] = useState('');
  const [viewingQuestion, setViewingQuestion] = useState(null);

  const lastCalledQB = useRef(null);
  const prevDraftQuestionBankRef = useRef(null);
  const bankNameInputRef = useRef(null);
  const loadingBankIdRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const lastNonZeroQuestionsCount = useRef(0);
  const currentQuestionsCount = useMemo(() => {
    return questions.filter(q => (q.status || 'Active') === 'Active').length;
  }, [questions]);

  useEffect(() => {
    if (currentQuestionsCount > 0) {
      lastNonZeroQuestionsCount.current = currentQuestionsCount;
    }
  }, [currentQuestionsCount]);

  const displayedQuestionsCount = (contentLoading && currentQuestionsCount === 0)
    ? lastNonZeroQuestionsCount.current
    : currentQuestionsCount;


  const renderQuestionRow = (q, idx) => {
    const isEditingRow = editingQuestionId === q.id;
    const isActive = (q.status || 'Active') === 'Active';

    if (isEditingRow) {
      return (
        <div key={q.id} className="assess-mod-qb-questions-table-row sticky top-0 z-10 shadow-md !bg-white border-l-4 border-l-[#b91c1c]">
          {/* Col 1: Index */}
          <div className="assess-mod-qb-table-col--index">
            <span className="text-[14px] font-bold text-[#1e293b]">{idx + 1}</span>
          </div>

          {/* Col 2: Question Type (Disabled Dropdown) */}
          <div className="relative">
            <select
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-semibold text-[#1e293b] outline-none shadow-sm cursor-not-allowed"
              value={q.type}
              disabled
            >
              <option value="MCQ">MCQ</option>
              <option value="DESCRIPTIVE">Descriptive</option>
            </select>
          </div>

          {/* Col 3: Pedagogy */}
          <div className="assess-mod-qb-table-col--pedagogy relative" ref={pedagogyRef}>
            <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
              <input
                type="text"
                placeholder="Pedagogy..."
                className={`w-full bg-[#f8fafc] border rounded-lg px-2 py-1.5 text-xs font-semibold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-200'}`}
                value={pedagogySearch}
                onChange={(e) => {
                  setPedagogySearch(e.target.value);
                  if (!showPedagogyDropdown) {
                    setShowPedagogyDropdown(true);
                    requestAnimationFrame(() => syncPedagogyDropdownPosition());
                  }
                }}
                onFocus={() => {
                  setShowPedagogyDropdown(true);
                  requestAnimationFrame(() => syncPedagogyDropdownPosition());
                }}
                onClick={togglePedagogyDropdown}
              />
            </div>
            {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
              <PedagogyDropdownMenu
                menuRef={pedagogyMenuRef}
                position={pedagogyDropdownPos}
                filteredOptions={filteredPedagogyOptions}
                {...pedagogyDropdownMenuProps}
              />,
              document.body
            )}
          </div>

          {/* Col 4: Question Content */}
          <div className="assess-mod-qb-table-col--content min-w-0">
            <textarea
              placeholder="Question..."
              rows={3}
              className="w-full bg-[#f8fafc] border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-y min-h-[72px]"
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
            />
          </div>

          {/* Col 5: Option Details */}
          <div className="assess-mod-qb-table-col--options min-w-0">
            {q.type === 'MCQ' ? (
              <>
                <div className="grid grid-cols-2 gap-1.5">
                  {['A', 'B', 'C', 'D'].map((opt) => (
                    <div key={opt} className="relative">
                      <input
                        type="text"
                        placeholder={`${opt} ...`}
                        className={`w-full bg-white border rounded-lg pl-5 pr-5 py-1 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 ${correctOption === opt ? 'border-red-400 ring-2 ring-red-50' : 'border-slate-200'}`}
                        value={newOptions[opt]}
                        onChange={(e) => setNewOptions({ ...newOptions, [opt]: e.target.value })}
                      />
                      <span className={`absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] font-black ${correctOption === opt ? 'text-[#b91c1c]' : 'text-slate-300'}`}>{opt}</span>
                      <div
                        onClick={() => setCorrectOption(opt)}
                        className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border cursor-pointer transition-all flex items-center justify-center ${correctOption === opt ? 'border-red-600 bg-[#b91c1c]' : 'border-slate-300 bg-white hover:border-red-400'}`}
                      >
                        {correctOption === opt && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[9px] font-semibold text-[#b91c1c] mt-1.5">
                  <span className="font-black text-[#b91c1c]">Note:</span> Please select the correct option by clicking the corresponding radio button.
                </p>
              </>
            ) : (
              <textarea
                placeholder="Expected Answer..."
                className="w-full bg-[#f8fafc] border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-y min-h-[72px]"
                value={newCorrectAnswer}
                onChange={(e) => setNewCorrectAnswer(e.target.value)}
              />
            )}
          </div>

          {/* Col 6: Actions */}
          <div className="assess-mod-qb-table-actions" onDoubleClick={(e) => e.stopPropagation()}>
            <div className="assess-mod-qb-table-actions-btns">
              <AssessmentConfirmIconButton
                onClick={handleManualSave}
                title="Save"
              />
              <AssessmentCancelIconButton
                onClick={handleCancelInlineEdit}
                title="Cancel"
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={q.id}
        onDoubleClick={() => setViewingQuestion(q)}
        className={`assess-mod-qb-questions-table-row cursor-pointer select-none ${!isActive ? 'assess-mod-qb-questions-table-row--inactive' : ''}`}
      >
        {/* Col 1: Index */}
        <div className="assess-mod-qb-table-col--index">
          <span className="text-[14px] font-bold text-[#1e293b]">{idx + 1}</span>
        </div>

        {/* Col 2: Type Tag */}
        <div className="flex items-center">
          <span className={`text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md border ${q.type === 'MCQ' ? 'bg-red-50 text-[#b91c1c] border-red-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
            {q.type}
          </span>
        </div>

        {/* Col 3: Pedagogy */}
        <div className="assess-mod-qb-table-col--pedagogy">
          <span className="assess-mod-qb-pedagogy-name">{q.pedagogy || 'General'}</span>
        </div>

        {/* Col 4: Question Content */}
        <div className="assess-mod-qb-table-col--content min-w-0">
          <p className="assess-mod-qb-table-question" title={q.question.replace(/\s*\(Q\d+\)$/i, '')}>
            {q.question.replace(/\s*\(Q\d+\)$/i, '')}
          </p>
          <div className="assess-mod-qb-table-meta">
            <span>{getQuestionMarksLabel(q.marks)}</span>
          </div>
        </div>

        {/* Col 5: Option Details */}
        <div className="assess-mod-qb-table-col--options min-w-0">
          {isMcqQuestionType(q.type) ? (
            <div className="assess-mod-qb-options-grid">
              {MCQ_OPTION_LETTERS.map((letter) => {
                const optionText = getMcqOptionText(q, letter);
                if (!optionText) return null;
                const isCorrect = isMcqCorrectOption(q, letter);
                return (
                  <div
                    key={letter}
                    className={`assess-mod-qb-option-cell ${isCorrect ? 'assess-mod-qb-option-cell--correct' : ''}`}
                    title={optionText}
                  >
                    <span className="assess-mod-qb-option-cell-label">{letter.toLowerCase()})</span>
                    <span className="assess-mod-qb-option-cell-text">{optionText}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="assess-mod-qb-desc-answer">
              <span className="font-semibold text-slate-600">Expected: </span>
              {q.correct_answer || '—'}
            </div>
          )}
        </div>

        {/* Col 6: Actions */}
        <div
          className="assess-mod-qb-table-actions"
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <div className="assess-mod-qb-table-actions-status">
            <span className={`assess-mod-qb-table-actions-status-label ${isActive ? 'is-active' : 'is-inactive'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
            <button
              type="button"
              title={isActive ? 'Deactivate question' : 'Activate question'}
              onClick={() => handleToggleStatus(q)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${isActive ? 'bg-emerald-500' : 'bg-slate-300'}`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${isActive ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
          </div>
          <div className="assess-mod-qb-table-actions-btns">
            <AssessmentEditIconButton
              onClick={isActive ? () => handleEditQuestion(q) : undefined}
              title={isActive ? 'Edit question' : 'Activate question to edit'}
              className={!isActive ? '!opacity-30 !cursor-not-allowed pointer-events-none' : ''}
            />
            <AssessmentDeleteIconButton
              onClick={isActive ? () => handleDeleteQuestion(q.id) : undefined}
              title={isActive ? 'Delete question' : 'Activate question to delete'}
              className={!isActive ? '!opacity-30 !cursor-not-allowed pointer-events-none' : ''}
            />
          </div>
        </div>
      </div>
    );
  };

  const loadBankQuestions = useCallback(async (bankId, { updateCache = true } = {}) => {
    if (!bankId) return [];
    loadingBankIdRef.current = bankId;
    setContentLoading(true);
    try {
      const res = await testService.getQuestionBank(bankId);
      if (res?.status !== 'success' || !res.bank) {
        return [];
      }
      const rawQuestions = (res.bank.questions || []).map((q) => ({ ...q, status: q.status || 'Active' }));
      if (updateCache) {
        setBanks((prev) =>
          prev.map((b) => (String(b.id) === String(bankId) ? { ...b, rawQuestions, questions: res.bank.question_count ?? rawQuestions.length } : b))
        );
      }
      if (String(loadingBankIdRef.current) === String(bankId)) {
        return mapBankQuestionsFromRaw(rawQuestions);
      }
      return [];
    } catch (err) {
      console.error(err);
      toast.error('Failed to load question bank');
      return [];
    } finally {
      if (String(loadingBankIdRef.current) === String(bankId)) {
        setContentLoading(false);
        loadingBankIdRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedBankId && bankNameInputRef.current) {
      bankNameInputRef.current.focus();
    }
  }, [selectedBankId]);

  // Silently refresh the active bank's questions every 30s so external changes appear without a page reload
  useEffect(() => {
    if (!selectedBankId) return;
    const bankPollInterval = setInterval(() => {
      if (!document.hidden && !isAddingQuestion && !editingQuestionId) {
        testService.getQuestionBank(selectedBankId).then(res => {
          if (res?.status === 'success' && res.bank) {
            const rawQuestions = (res.bank.questions || []).map(q => ({ ...q, status: q.status || 'Active' }));
            const mapped = mapBankQuestionsFromRaw(rawQuestions);
            
            setQuestions(prev => {
              const activeBank = banks.find(b => String(b.id) === String(selectedBankId));
              const origCache = activeBank ? (activeBank.rawQuestions || []) : [];
              const merged = mergeQuestions(prev, mapped, editingQuestionId, mapBankQuestionsFromRaw(origCache));
              if (areQuestionsEqual(prev, merged)) return prev;
              return merged;
            });

            setBanks(prev => {
              const existingBank = prev.find(b => String(b.id) === String(selectedBankId));
              if (existingBank && areQuestionsEqual(existingBank.rawQuestions, rawQuestions)) {
                return prev;
              }
              return prev.map(b =>
                String(b.id) === String(selectedBankId)
                  ? { ...b, rawQuestions, questions: res.bank.question_count ?? rawQuestions.length }
                  : b
              );
            });
          }
        }).catch(err => console.error('QB question poll error:', err));
      }
    }, 30000);
    return () => clearInterval(bankPollInterval);
  }, [selectedBankId, isAddingQuestion, editingQuestionId, banks]);

  // Synchronize from draftQuestionBank prop (parent → child, wizard mode only).
  // This effect intentionally does NOT include local state (selectedBankId, bankName, questions)
  // in its dependency array. It must only fire when the PARENT changes draftQuestionBank,
  // not when the user selects a bank locally (which would create a reset loop).
  useEffect(() => {
    if (!isWizardMode) return;

    const prev = prevDraftQuestionBankRef.current;
    prevDraftQuestionBankRef.current = draftQuestionBank;

    // Nothing changed from parent's perspective — skip.
    if (draftQuestionBank === prev) return;

    if (!draftQuestionBank) {
      if (prev) {
        setSelectedBankId(null);
        setBankName('');
        setQuestions([]);
        setIsAddingQuestion(false);
      }
      return;
    }

    const prevId = String(prev?.id || '');
    const newId  = String(draftQuestionBank.id || '');
    const idChanged = newId !== prevId;

    // If parent is pushing a bank that the user already selected locally, only
    // sync questions if the parent now has question data we don't have yet.
    if (!idChanged) {
      // Same bank — only update questions if parent provided them and we have none.
      if (
        draftQuestionBank.questions &&
        draftQuestionBank.questions.length > 0
      ) {
        setQuestions((cur) =>
          areQuestionsEqual(cur, draftQuestionBank.questions) ? cur : draftQuestionBank.questions
        );
      }
      return;
    }

    // Parent switched to a different bank — fully sync.
    setSelectedBankId(draftQuestionBank.id || null);
    setBankName(draftQuestionBank.name || '');

    if (draftQuestionBank.questions && draftQuestionBank.questions.length > 0) {
      setQuestions(draftQuestionBank.questions);
    } else if (draftQuestionBank.id) {
      setQuestions([]);
      loadBankQuestions(draftQuestionBank.id).then((mapped) => {
        setQuestions(mapped);
      });
    } else {
      setQuestions([]);
    }
  }, [draftQuestionBank, isWizardMode, loadBankQuestions]);

  useEffect(() => {
    if (isWizardMode && onSelectQB) {
      if (selectedBankId || bankName.trim()) {
        const qbPayload = {
          id: selectedBankId,
          name: bankName,
          questions: questions
        };
        
        const isIdentical = lastCalledQB.current &&
          lastCalledQB.current.id === qbPayload.id &&
          lastCalledQB.current.name === qbPayload.name &&
          lastCalledQB.current.questions.length === qbPayload.questions.length &&
          lastCalledQB.current.questions.every((q, idx) => {
            const other = qbPayload.questions[idx];
            return other &&
              q.id === other.id &&
              q.status === other.status &&
              q.question === other.question &&
              q.type === other.type &&
              q.pedagogy === other.pedagogy &&
              q.marks === other.marks &&
              q.option_a === other.option_a &&
              q.option_b === other.option_b &&
              q.option_c === other.option_c &&
              q.option_d === other.option_d &&
              q.correct_answer === other.correct_answer;
          });
          
        if (!isIdentical) {
          lastCalledQB.current = qbPayload;
          onSelectQB(qbPayload);
        }
      } else {
        if (lastCalledQB.current !== null) {
          lastCalledQB.current = null;
          onSelectQB(null);
        }
      }
    }
  }, [selectedBankId, bankName, questions, isWizardMode, onSelectQB]);

  
  const fetchBanks = async () => {
    setSidebarLoading(true);
    try {
      const res = await testService.getQuestionBanks();
      if (res.status === 'success') {
        setBanks((prev) => {
          const fresh = (res.banks || []).map(mapBankListItem);
          return fresh.map((f) => {
            const existing = prev.find((b) => String(b.id) === String(f.id));
            if (existing) {
              return {
                ...f,
                rawQuestions: existing.rawQuestions || f.rawQuestions,
              };
            }
            return f;
          });
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSidebarLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
    // Background poll — keeps sidebar bank list current without a hard reload
    const pollInterval = setInterval(() => {
      if (!document.hidden) {
        testService.getQuestionBanks().then(res => {
          if (res?.status === 'success') {
            setBanks((prev) => {
              const fresh = (res.banks || []).map(mapBankListItem);
              return fresh.map((f) => {
                const existing = prev.find((b) => String(b.id) === String(f.id));
                if (existing) {
                  return {
                    ...f,
                    rawQuestions: existing.rawQuestions || f.rawQuestions,
                  };
                }
                return f;
              });
            });
          }
        }).catch(err => console.error('QB poll error:', err));
      }
    }, 30000);
    return () => clearInterval(pollInterval);
  }, []);

  const handleBulkUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = isExcel
        ? parseQuestionBankExcel(e.target.result)
        : parseQuestionBankCsv(e.target.result);

      if (!result.success) {
        toast.error(result.error);
        return;
      }

      setQuestions((prev) => [...prev, ...result.questions]);
      toast.success(`${result.questions.length} questions imported to draft.`);
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }

    event.target.value = null;
  };

  const pedagogyRef = useRef(null);
  const pedagogyAnchorRef = useRef(null);
  const pedagogyMenuRef = useRef(null);
  const [pedagogyDropdownPos, setPedagogyDropdownPos] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadPedagogies = async () => {
      try {
        const res = await testService.getPedagogies();
        if (!cancelled && res?.status === 'success' && Array.isArray(res.pedagogies) && res.pedagogies.length > 0) {
          setPedagogyOptions(res.pedagogies);
        }
      } catch (err) {
        console.error('Failed to load pedagogies:', err);
      }
    };
    loadPedagogies();
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmedPedagogySearch = pedagogySearch.trim();
  const filteredPedagogyOptions = pedagogyOptions.filter((opt) =>
    opt.toLowerCase().includes(trimmedPedagogySearch.toLowerCase())
  );
  const canSaveNewPedagogy =
    trimmedPedagogySearch.length > 0 &&
    !pedagogyOptions.some((opt) => opt.toLowerCase() === trimmedPedagogySearch.toLowerCase());

  const handleSelectPedagogy = useCallback((value) => {
    setPedagogySearch(value);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
  }, []);

  const handleSaveNewPedagogy = useCallback(async () => {
    if (!canSaveNewPedagogy || savingPedagogy) return;
    setSavingPedagogy(true);
    try {
      const res = await testService.savePedagogy(trimmedPedagogySearch);
      if (res?.status === 'success') {
        if (Array.isArray(res.pedagogies)) {
          setPedagogyOptions(res.pedagogies);
        } else if (res.pedagogy) {
          setPedagogyOptions((prev) => {
            const next = [...prev, res.pedagogy];
            return next.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          });
        }
        setPedagogySearch(res.pedagogy || trimmedPedagogySearch);
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        toast.success(res.created === false ? 'Pedagogy already exists' : 'Pedagogy saved');
      } else {
        toast.error(res?.message || 'Failed to save pedagogy');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save pedagogy');
    } finally {
      setSavingPedagogy(false);
    }
  }, [canSaveNewPedagogy, savingPedagogy, trimmedPedagogySearch]);

  const syncPedagogyDropdownPosition = useCallback(() => {
    const anchor = pedagogyAnchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuMaxHeight = 240;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuMaxHeight + gap && rect.top > menuMaxHeight + gap;
    setPedagogyDropdownPos({
      left: rect.left,
      width: rect.width,
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
    });
  }, []);

  const exitPedagogyDeleteMode = useCallback(() => {
    setPedagogyDeleteMode(false);
    setSelectedPedagogiesForDelete([]);
  }, []);

  const togglePedagogyDeleteMode = useCallback(() => {
    setPedagogyDeleteMode((prev) => {
      const next = !prev;
      if (next) {
        setSelectedPedagogiesForDelete([]);
        setShowPedagogyDropdown(true);
        requestAnimationFrame(() => syncPedagogyDropdownPosition());
      } else {
        setSelectedPedagogiesForDelete([]);
      }
      return next;
    });
  }, [syncPedagogyDropdownPosition]);

  const togglePedagogyDeleteSelect = useCallback((name) => {
    setSelectedPedagogiesForDelete((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  }, []);

  const handleConfirmDeletePedagogies = useCallback(async () => {
    if (selectedPedagogiesForDelete.length === 0 || deletingPedagogies) return;
    setDeletingPedagogies(true);
    try {
      const res = await testService.deletePedagogies(selectedPedagogiesForDelete);
      if (res?.status === 'success') {
        if (Array.isArray(res.pedagogies)) {
          setPedagogyOptions(res.pedagogies);
        }
        const deletedLower = new Set(
          (res.deleted || selectedPedagogiesForDelete).map((n) => String(n).toLowerCase())
        );
        if (deletedLower.has((pedagogySearch || '').toLowerCase())) {
          setPedagogySearch('');
        }
        exitPedagogyDeleteMode();
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        const count = res.deleted?.length ?? selectedPedagogiesForDelete.length;
        toast.success(count === 1 ? 'Pedagogy removed' : `${count} pedagogies removed`);
      } else {
        toast.error(res?.message || 'Failed to delete pedagogies');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete pedagogies');
    } finally {
      setDeletingPedagogies(false);
    }
  }, [
    selectedPedagogiesForDelete,
    deletingPedagogies,
    pedagogySearch,
    exitPedagogyDeleteMode,
  ]);

  const pedagogyDropdownMenuProps = {
    deleteMode: pedagogyDeleteMode,
    selectedForDelete: selectedPedagogiesForDelete,
    onToggleDeleteSelect: togglePedagogyDeleteSelect,
    onConfirmDelete: handleConfirmDeletePedagogies,
    deletingSelected: deletingPedagogies,
    onCancelDeleteMode: exitPedagogyDeleteMode,
    canSaveNew: pedagogyDeleteMode ? false : canSaveNewPedagogy,
    savingNew: savingPedagogy,
    onSelect: handleSelectPedagogy,
    onSaveNew: handleSaveNewPedagogy,
    onToggleDeleteMode: togglePedagogyDeleteMode,
  };

  const togglePedagogyDropdown = () => {
    setShowPedagogyDropdown((open) => {
      const next = !open;
      if (next) {
        requestAnimationFrame(() => syncPedagogyDropdownPosition());
      } else {
        setPedagogyDropdownPos(null);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!showPedagogyDropdown) return undefined;
    syncPedagogyDropdownPosition();
    const onScrollOrResize = () => syncPedagogyDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showPedagogyDropdown, syncPedagogyDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inField = pedagogyRef.current?.contains(event.target);
      const inMenu = pedagogyMenuRef.current?.contains(event.target);
      if (!inField && !inMenu) {
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        setPedagogyDeleteMode(false);
        setSelectedPedagogiesForDelete([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddQuestion = (type = 'MCQ') => {
    setEditingQuestionId(null);
    setIsAddingQuestion(true);
    setNewQuestionType(type);
    setPedagogySearch('');
    setCorrectOption(null);
    setNewQuestion('');
    setNewOptions({ A: '', B: '', C: '', D: '' });
    setNewCorrectAnswer('');
    setNewMarks(type === 'MCQ' ? 1 : 2);
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
  };

  const handleCancelInlineEdit = () => {
    setEditingQuestionId(null);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
  };

  const selectBank = async (bank) => {
    // Pre-update the ref so the draftQuestionBank sync effect won't treat
    // this user-initiated selection as an incoming change from the parent
    // and overwrite our local state.
    prevDraftQuestionBankRef.current = { id: bank.id, name: bank.name, questions: [] };

    setBankName(bank.name);
    setSelectedBankId(bank.id);
    if (bank.rawQuestions?.length) {
      const mapped = mapBankQuestionsFromRaw(bank.rawQuestions);
      setQuestions(mapped);
      return;
    }
    setQuestions([]);
    if ((bank.questions || 0) === 0) {
      return;
    }
    const mapped = await loadBankQuestions(bank.id);
    setQuestions(mapped);
  };

  const handleToggleStatus = async (q) => {
    const newStatus = (q.status || 'Active') === 'Active' ? 'Inactive' : 'Active';
    // Optimistically update UI
    setQuestions((prev) => prev.map((item) => item.id === q.id ? { ...item, status: newStatus } : item));
    // If question has a real DB id (not a local timestamp), persist
    const isRealDbId = typeof q.id === 'number' && q.id < 1000000000000;
    if (q.id && isRealDbId) {
      try {
        await testService.updateQBQuestionStatus(q.id, newStatus);
        toast.success(newStatus === 'Active' ? 'Question activated.' : 'Question deactivated.');
      } catch (err) {
        console.error(err);
        // Rollback
        setQuestions((prev) => prev.map((item) => item.id === q.id ? { ...item, status: q.status || 'Active' } : item));
        toast.error('Failed to update question status.');
      }
    } else {
      toast.success(newStatus === 'Active' ? 'Question activated.' : 'Question deactivated.');
    }
  };

  const handleEditQuestion = (q) => {
    setIsAddingQuestion(false);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
    setNewQuestionType(q.type);
    setEditingQuestionId(q.id);
    setNewQuestion(q.question.replace(/\s*\(Q\d+\)$/i, ''));
    setPedagogySearch(q.pedagogy || '');
    setNewMarks(q.marks);
    if (isMcqQuestionType(q.type)) {
      setNewOptions({ A: q.option_a, B: q.option_b, C: q.option_c, D: q.option_d });
      setCorrectOption(resolveMcqCorrectLetter(q) || q.correct_answer);
    } else {
      setNewCorrectAnswer(q.correct_answer || '');
    }
  };

  const handleDeleteQuestion = (id) => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this question?',
      message: 'Are you sure you want to delete this question? This will remove it from the draft.',
      confirmText: 'Yes, delete it!',
      onConfirm: () => {
        setQuestions(prev => prev.filter(q => q.id !== id));
        if (editingQuestionId === id) handleCancelInlineEdit();
        setPopupConfig({ isOpen: false });
        toast.success('Question deleted successfully');
      }
    });
  };

  const handleManualSave = () => {
    if (!newQuestion.trim()) return toast.error("Question text is required");
    const finalType = editingQuestionId
      ? (questions.find(q => q.id === editingQuestionId)?.type || 'MCQ')
      : newQuestionType;

    if (finalType === 'MCQ') {
      if (!newOptions.A || !newOptions.B || !newOptions.C || !newOptions.D) return toast.error("All options are required for MCQ");
      if (!correctOption) return toast.error("Please select a correct option");
    }

    const existingQuestion = editingQuestionId ? questions.find(q => q.id === editingQuestionId) : null;

    const questionObj = normalizeMcqQuestionRow({
      id: editingQuestionId || Date.now(),
      section_name: 'General',
      question: newQuestion,
      type: finalType,
      option_a: finalType === 'MCQ' ? newOptions.A : '',
      option_b: finalType === 'MCQ' ? newOptions.B : '',
      option_c: finalType === 'MCQ' ? newOptions.C : '',
      option_d: finalType === 'MCQ' ? newOptions.D : '',
      correct_answer: finalType === 'MCQ' ? correctOption : newCorrectAnswer,
      marks: editingQuestionId ? newMarks : (finalType === 'MCQ' ? 1 : 2),
      pedagogy: pedagogySearch || 'General',
      status: existingQuestion?.status || 'Active',
    });

    if (editingQuestionId) {
      setQuestions((prev) => prev.map((q) => (q.id === editingQuestionId ? questionObj : q)));
    } else {
      setQuestions((prev) => [...prev, questionObj]);
    }

    const wasEditing = !!editingQuestionId;
    setIsAddingQuestion(false);
    setEditingQuestionId(null);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
    toast.success(wasEditing ? 'Question updated.' : 'Question added to draft.');
  };

  const handleRenameBank = (bank) => {
    setPopupInput(bank.name);
    setPopupConfig({
      isOpen: true,
      type: 'info',
      title: 'Rename Question Bank',
      message: 'Enter the new name for this question bank.',
      showInput: true,
      inputPlaceholder: 'Bank Name...',
      confirmText: 'Rename',
      onConfirm: async () => {
        if (!popupInput.trim()) return toast.error("Name cannot be empty");
        const isDuplicate = banks.some(b => 
          b.name.trim().toLowerCase() === popupInput.trim().toLowerCase() && 
          String(b.id) !== String(bank.id)
        );
        if (isDuplicate) {
          return toast.error("A question bank with this name already exists. Please choose a unique name.");
        }
        try {
          await testService.renameQuestionBank(bank.id, popupInput);
          fetchBanks();
          setPopupConfig({
            isOpen: true,
            type: 'success',
            title: 'Renamed!',
            message: 'Question bank has been renamed successfully.',
            showCancel: false,
            confirmText: 'OK',
            onConfirm: () => setPopupConfig({ isOpen: false })
          });
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
        }
      }
    });
  };

  const handleDownloadTemplate = () => {
    const sampleRows = [
      ['General', 'What is 2+2?', 'MCQ', '3', '4', '5', '6', 'B', '1', '', 'Factual'],
      [
        'General',
        'Explain gravity.',
        'Descriptive question',
        '',
        '',
        '',
        '',
        'Force that pulls objects towards each other.',
        '2',
        'Force that pulls objects towards each other.',
        'Conceptual',
      ],
    ];
    const csvContent = [
      QB_CSV_INSTRUCTION,
      QB_CSV_HEADERS.map(escapeCsvCell).join(','),
      ...sampleRows.map((row) => row.map(escapeCsvCell).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.setAttribute('href', objectUrl);
    link.setAttribute('download', buildQuestionBankTemplateFileName(bankName));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handleSave = async () => {
    if (!bankName.trim()) {
      toast.error("Please provide a name for the Question Bank.");
      return;
    }
    
    const isDuplicate = banks.some(b => 
      b.name.trim().toLowerCase() === bankName.trim().toLowerCase() && 
      String(b.id) !== String(selectedBankId)
    );
    if (isDuplicate) {
      toast.error("A question bank with this name already exists. Please choose a unique name.");
      return;
    }

    if (questions.length === 0) {
      toast.error("Please add at least one question to the bank.");
      return;
    }

    setContentLoading(true);
    try {
      let repoId = selectedBankId;
      if (!repoId) {
        const bankRes = await testService.saveQuestionBank({ name: bankName });
        if (bankRes.status === 'success') {
          repoId = bankRes.id;
        } else {
          throw new Error("Failed to create bank");
        }
      }

      if (repoId) {
        const bulkRes = await testService.bulkSaveQBQuestions({
          repository_id: repoId,
          questions: questions
        });
        if (bulkRes.status === 'success') {
          toast.success('Question Bank updated successfully.');
          setQuestions([]);
          setBankName('');
          setSelectedBankId(null);
          fetchBanks();
          if (returnToTemplates) {
            returnFromModule();
          }
        } else {
          toast.error(bulkRes.message || "Failed to save bank.");
        }
      }
    } catch (err) {
      toast.error("Failed to save bank.");
    } finally {
      setContentLoading(false);
    }
  };

  const handleDeleteBank = (id, e) => {
    e.stopPropagation();
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this bank?',
      message: 'Are you sure you want to delete this bank? All associated questions will be affected.',
      confirmText: 'Yes, delete it!',
      onConfirm: async () => {
        try {
          const res = await testService.deleteQuestionBank(id);
          setPopupConfig({ isOpen: false });
          if (res?.status === 'success') {
            if (String(selectedBankId) === String(id)) {
              setSelectedBankId(null);
              setBankName('');
              setQuestions([]);
            }
            fetchBanks();
            toast.success('Question bank deleted successfully');
          } else {
            toast.error(res?.message || 'Failed to delete question bank');
          }
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
          toast.error('Failed to delete question bank');
        }
      }
    });
  };

  const filteredBanks = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return banks;
    return banks.filter((bank) => String(bank.name || '').toLowerCase().includes(query));
  }, [banks, searchTerm]);



  const handleCreateNewBank = () => {
    setBankName('');
    setSelectedBankId(null);
    setQuestions([]);
    setIsAddingQuestion(false);
    setEditingQuestionId(null);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
    if (isWizardMode && onSelectQB) {
      onSelectQB(null);
    }
  };

  return (
    <div className={isWizardMode ? "flex flex-col flex-1 min-h-0 bg-[#f8fafc] overflow-hidden font-sans rounded-3xl border border-slate-100 shadow-xl" : "assess-mod-page assess-mod-qb-page flex flex-col h-full min-h-0 bg-[#f8fafc] overflow-hidden font-sans"}>
      {/* Top Header */}
      {!isWizardMode && (
        <header className="assess-mod-header h-[70px] bg-white border-b border-slate-200 flex items-center justify-between px-10 shrink-0">
          <button
            type="button"
            onClick={onBack}
            className="assess-mod-back-mobile lg:hidden"
            aria-label="Go back"
          >
            <FiArrowLeft className="text-lg" />
          </button>
          <div className="assess-mod-header-brand flex items-center gap-4">
            <div className="w-10 h-10 bg-[#b91c1c] rounded-xl flex items-center justify-center shadow-lg shadow-red-100">
              <FiBookOpen className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-[16px] font-black text-[#1e293b] tracking-tight leading-none mb-0">
                Question bank
              </h1>
            </div>
          </div>
          <div className="assess-mod-header-actions flex items-center gap-3">
            {!isWizardMode && (
              <button
                type="button"
                onClick={onBack}
                className="btn-assessment btn-assessment-danger hidden lg:inline-flex"
              >
                <FiArrowLeft className="text-sm" /> Back
              </button>
            )}
          </div>
        </header>
      )}

      <div className="assess-mod-body flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar */}
        <aside className="assess-mod-sidebar w-full lg:w-[300px] bg-[#f8fafc] border-e border-slate-200 flex flex-col shrink-0 overflow-hidden">
          <div className="assess-mod-sidebar-search bg-white px-6 pt-5 pb-4 border-b border-slate-200 shrink-0">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-[13px] font-black text-[#1e293b] uppercase tracking-widest mb-0 shrink min-w-0 leading-tight">
                Saved Question Banks
              </h2>
              <button
                type="button"
                onClick={handleCreateNewBank}
                className="assess-mod-sidebar-create-btn"
              >
                <FiPlus /> Create QB
              </button>
            </div>
          </div>

          <div className="assess-mod-sidebar-list flex-1 min-h-0 px-6 pt-4 pb-6 overflow-y-auto custom-scrollbar">
            <div className="relative mb-3 assess-mod-sidebar-search-field">
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#b91c1c] text-xs pointer-events-none" />
              <input
                type="text"
                placeholder="Search banks..."
                className="w-full h-8 bg-white border border-red-200 rounded-lg pl-8 pr-2.5 text-[11px] font-semibold text-slate-700 shadow-sm ring-1 ring-red-50 outline-none transition-all placeholder:text-slate-400 focus:border-[#b91c1c] focus:ring-2 focus:ring-red-100"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {filteredBanks.length > 0 && (
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3 px-0.5">
                {filteredBanks.length} bank{filteredBanks.length !== 1 ? 's' : ''}
              </p>
            )}
            <div className="space-y-2">
              {sidebarLoading ? (
              <p className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest py-6 px-2">
                Loading banks...
              </p>
            ) : filteredBanks.length === 0 ? (
                <p className="text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest py-6 px-2">
                  {banks.length === 0 ? 'No question banks yet' : 'No banks match your search'}
                </p>
              ) : (
                filteredBanks.map((bank) => (
                  <div
                    key={bank.id}
                    onClick={() => selectBank(bank)}
                    className={`relative overflow-hidden p-3 rounded-xl border transition-all cursor-pointer group flex items-center gap-3 shadow-sm ${String(selectedBankId) === String(bank.id) ? 'bg-red-50 border-red-200 ring-1 ring-red-100' : 'bg-white border-slate-200 hover:border-red-200 hover:shadow-md'}`}
                  >
                    <div className="assess-mod-sidebar-item-icon">
                      <FiFileText />
                    </div>
                    <div className="flex-1 min-w-0 pr-2 group-hover:pr-12 transition-all duration-300">
                      <h5 className={`text-[13px] font-black mb-0 tracking-tight truncate ${String(selectedBankId) === String(bank.id) ? 'text-[#b91c1c]' : 'text-[#1e293b]'}`}>{bank.name}</h5>
                      <p className="text-[8px] text-slate-400 font-black uppercase tracking-widest mt-0.5 truncate">
                        {bank.questions} QUESTIONS • {bank.sections} SECTIONS
                      </p>
                    </div>
                    <div 
                      className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all bg-transparent z-10" 
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => handleDeleteBank(bank.id, e)}
                        title="Delete question bank"
                        className="w-8 h-8 bg-transparent text-red-500 rounded-lg flex items-center justify-center border border-transparent hover:bg-red-50 transition-all"
                      >
                        <FiTrash2 className="text-[10px]" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="assess-mod-main-wrap assess-mod-qb-main flex-1 min-w-0 min-h-0 overflow-hidden bg-[#f8fafc] flex flex-col">
          <div className="assess-mod-card-scroll assess-mod-qb-card-scroll flex-1 min-h-0 flex flex-col w-full max-w-none">
          <div className="assess-mod-card assess-mod-qb-card flex-1 min-h-0 w-full bg-white border border-slate-100 shadow-2xl shadow-slate-200/40 flex flex-col overflow-hidden">
            
            {/* Repository Setup Header */}
            <div className="assess-mod-qb-topbar shrink-0">
              <div className="assess-mod-setup-row assess-mod-qb-setup-row flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 bg-rose-50 text-[#b91c1c] rounded-xl flex items-center justify-center border border-rose-100 shadow-sm shrink-0">
                    <FiFileText className="text-lg" />
                  </div>

                  <div className="flex-1 max-w-[280px]">
                    <div className="mb-1">
                      <h6 className="text-[11px] font-black text-[#1e293b] uppercase tracking-widest mb-0">QUESTION BANK NAME</h6>
                    </div>
                    <input 
                      ref={bankNameInputRef}
                      type="text"
                      placeholder="Enter Question Bank Name..."
                      readOnly={!!selectedBankId}
                      className={`w-full border border-slate-100 rounded-xl px-4 py-2 text-[12px] font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 shadow-inner ${
                        selectedBankId
                          ? 'bg-slate-50 cursor-default focus:ring-0'
                          : 'bg-[#f8fafc] focus:ring-2 focus:ring-red-100'
                      }`}
                      value={bankName}
                      onChange={(e) => {
                        if (!selectedBankId) setBankName(e.target.value);
                      }}
                    />
                  </div>

                  <div className="assess-mod-stats-pill flex items-center bg-white rounded-[24px] border border-slate-100 overflow-hidden shadow-sm shrink-0">
                    <div className="px-6 py-2.5 text-center border-e border-slate-50">
                      <span className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">SECTIONS</span>
                      <span className="text-xl font-black text-[#1e293b] leading-none">
                        {(() => {
                          const activeQs = questions.filter(q => (q.status || 'Active') === 'Active');
                          const types = new Set(activeQs.map(q => {
                            const t = String(q.type || 'MCQ').toUpperCase();
                            return (t === 'DESCRIPTIVE' || t.includes('DESCRIPTIVE') || t === 'SHORT ANSWER') ? 'DESCRIPTIVE' : 'MCQ';
                          }));
                          return types.size;
                        })()}
                      </span>
                    </div>
                    <div className="px-6 py-2.5 text-center">
                      <span className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">QUESTIONS</span>
                      <span className="text-xl font-black text-[#b91c1c] leading-none">
                        {displayedQuestionsCount}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="assess-mod-actions-row flex items-center gap-3 shrink-0">
                  <button 
                    onClick={handleDownloadTemplate}
                    className="btn-assessment btn-assessment-outline-purple !py-2.5 !px-6"
                  >
                    <FiDownload className="text-sm" /> Template
                  </button>
                  <button 
                    onClick={() => fileInputRef.current.click()}
                    className="btn-assessment btn-assessment-outline-purple !py-2.5 !px-6"
                  >
                    <FiCloud className="text-sm" /> Bulk Upload
                  </button>
                  <input 
                    type="file"
                    ref={fileInputRef}
                    onChange={handleBulkUpload}
                    accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    className="hidden"
                  />
                </div>
              </div>


            </div>

            {/* Table Area — fills card; scrolls inside question list */}
            <div className="assess-mod-table-panel flex-1 min-h-0 flex flex-col w-full min-w-0">
              <div className="assess-mod-table-panel-inner flex-1 min-h-0 flex flex-col bg-[#f8fafc]/50 rounded-[32px] border border-slate-100 w-full min-w-0 overflow-hidden">
                <div ref={scrollContainerRef} className="assess-mod-question-area assess-mod-qb-question-area flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 pt-0 pb-0 w-full min-w-0 custom-scrollbar">
                  <div className={`assess-mod-question-scroll flex flex-col flex-1 min-h-0 w-full min-w-0 ${isWizardMode ? 'pb-12' : 'pb-4'}`}>
                    {/* Single Table with Single Header */}
                    <div className="assess-mod-qb-questions-table assess-mod-qb-questions-panel--fill min-w-0 w-full flex-1 min-h-0 flex flex-col relative">
                      <div className="assess-mod-qb-questions-table-head">
                        <span>#</span>
                        <span>Type</span>
                        <span>Pedagogy</span>
                        <span>Question Content</span>
                        <span>Option/Expected Details</span>
                        <span className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-2">
                            <span>Actions</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => handleAddQuestion('MCQ')}
                            className="w-6 h-6 rounded-full bg-[#b91c1c] text-white hover:bg-[#991b1b] flex items-center justify-center transition-all shadow-sm shrink-0"
                            title="Add Question"
                          >
                            <FiPlus className="text-xs" />
                          </button>
                        </span>
                      </div>

                      <div className="assess-mod-qb-questions-table-body custom-scrollbar">
                        {/* Inline Add Question Row */}
                        {isAddingQuestion && (
                          <div className="assess-mod-qb-questions-table-row sticky top-0 z-10 shadow-md !bg-white border-l-4 border-l-[#b91c1c]">
                            {/* Col 1: Index */}
                            <div className="assess-mod-qb-table-col--index">
                              <span className="text-[14px] font-bold text-[#1e293b]">{questions.length + 1}</span>
                            </div>

                            {/* Col 2: Question Type Selector */}
                            <div className="relative">
                              <select
                                className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs font-semibold text-[#1e293b] outline-none shadow-sm cursor-pointer"
                                value={newQuestionType}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setNewQuestionType(val);
                                  setNewMarks(val === 'MCQ' ? 1 : 2);
                                }}
                              >
                                <option value="MCQ">MCQ</option>
                                <option value="DESCRIPTIVE">Descriptive</option>
                              </select>
                            </div>

                            {/* Col 3: Pedagogy Input */}
                            <div className="assess-mod-qb-table-col--pedagogy relative" ref={pedagogyRef}>
                              <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
                                <input
                                  type="text"
                                  placeholder="Pedagogy..."
                                  className={`w-full bg-[#f8fafc] border rounded-lg px-2 py-1.5 text-xs font-semibold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-200'}`}
                                  value={pedagogySearch}
                                  onChange={(e) => {
                                    setPedagogySearch(e.target.value);
                                    if (!showPedagogyDropdown) {
                                      setShowPedagogyDropdown(true);
                                      requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                    }
                                  }}
                                  onFocus={() => {
                                    setShowPedagogyDropdown(true);
                                    requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                  }}
                                  onClick={togglePedagogyDropdown}
                                />
                              </div>
                              {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
                                <PedagogyDropdownMenu
                                  menuRef={pedagogyMenuRef}
                                  position={pedagogyDropdownPos}
                                  filteredOptions={filteredPedagogyOptions}
                                  {...pedagogyDropdownMenuProps}
                                />,
                                document.body
                              )}
                            </div>

                            {/* Col 4: Question Content Textarea */}
                            <div className="assess-mod-qb-table-col--content min-w-0">
                              <textarea
                                placeholder="Question..."
                                rows={3}
                                className="w-full bg-[#f8fafc] border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-y min-h-[72px]"
                                value={newQuestion}
                                onChange={(e) => setNewQuestion(e.target.value)}
                              />
                            </div>

                            {/* Col 5: Dynamic Options / Answer Fields */}
                            <div className="assess-mod-qb-table-col--options min-w-0">
                              {newQuestionType === 'MCQ' ? (
                                <>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    {['A', 'B', 'C', 'D'].map((opt) => (
                                      <div key={opt} className="relative">
                                        <input
                                          type="text"
                                          placeholder={`${opt} ...`}
                                          className={`w-full bg-white border rounded-lg pl-5 pr-5 py-1 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 ${correctOption === opt ? 'border-red-400 ring-2 ring-red-50' : 'border-slate-200'}`}
                                          value={newOptions[opt]}
                                          onChange={(e) => setNewOptions({ ...newOptions, [opt]: e.target.value })}
                                        />
                                        <span className={`absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] font-black ${correctOption === opt ? 'text-[#b91c1c]' : 'text-slate-300'}`}>{opt}</span>
                                        <div
                                          onClick={() => setCorrectOption(opt)}
                                          className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border cursor-pointer transition-all flex items-center justify-center ${correctOption === opt ? 'border-red-600 bg-[#b91c1c]' : 'border-slate-300 bg-white hover:border-red-400'}`}
                                        >
                                          {correctOption === opt && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <p className="text-[9px] font-semibold text-[#b91c1c] mt-1.5">
                                    <span className="font-black text-[#b91c1c]">Note:</span> Please select the correct option by clicking the corresponding radio button.
                                  </p>
                                </>
                              ) : (
                                <textarea
                                  placeholder="Expected Answer..."
                                  className="w-full bg-[#f8fafc] border border-slate-200 rounded-lg px-3 py-2 text-xs font-medium text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-y min-h-[72px]"
                                  value={newCorrectAnswer}
                                  onChange={(e) => setNewCorrectAnswer(e.target.value)}
                                />
                              )}
                            </div>

                            {/* Col 6: Actions */}
                            <div className="assess-mod-qb-table-actions" onDoubleClick={(e) => e.stopPropagation()}>
                              <div className="assess-mod-qb-table-actions-btns">
                                <AssessmentConfirmIconButton
                                  onClick={handleManualSave}
                                  title="Save"
                                />
                                <AssessmentCancelIconButton
                                  onClick={() => setIsAddingQuestion(false)}
                                  title="Cancel"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* List Questions */}
                        {contentLoading && questions.length === 0 ? (
                          <>
                            {[1, 2, 3].map((n) => (
                              <div key={n} className="assess-mod-qb-questions-table-row animate-pulse bg-white border-b border-slate-50">
                                <div className="assess-mod-qb-table-col--index flex items-center justify-center">
                                  <div className="w-6 h-6 bg-slate-100 rounded-full"></div>
                                </div>
                                <div className="flex items-center">
                                  <div className="w-12 h-6 bg-slate-100 rounded-md"></div>
                                </div>
                                <div className="assess-mod-qb-table-col--pedagogy flex items-center">
                                  <div className="w-16 h-4 bg-slate-100 rounded"></div>
                                </div>
                                <div className="assess-mod-qb-table-col--content flex flex-col justify-center gap-2">
                                  <div className="w-5/6 h-4 bg-slate-100 rounded"></div>
                                  <div className="w-2/3 h-3 bg-slate-100 rounded"></div>
                                </div>
                                <div className="assess-mod-qb-table-col--options flex flex-col justify-center gap-1.5">
                                  <div className="w-11/12 h-3 bg-slate-100 rounded"></div>
                                  <div className="w-4/5 h-3 bg-slate-100 rounded"></div>
                                </div>
                                <div className="assess-mod-qb-table-actions flex items-center justify-end">
                                  <div className="w-14 h-6 bg-slate-100 rounded-lg"></div>
                                </div>
                              </div>
                            ))}
                          </>
                        ) : questions.length === 0 && !isAddingQuestion ? (
                          <div className="flex flex-col items-center justify-center min-h-[240px] py-12 opacity-40">
                            <FiInbox className="text-5xl mb-3 text-[#b91c1c]" />
                            <p className="text-[12px] font-black uppercase tracking-widest text-[#1e293b]">No Questions Added</p>
                          </div>
                        ) : (
                          (() => {
                            const mcqQs = questions.filter(q => q.type === 'MCQ');
                            const descQs = questions.filter(q => q.type !== 'MCQ');
                            return (
                              <>
                                {mcqQs.length > 0 && (
                                  <>
                                    <div className="assess-mod-paper-preview-section-head flex items-center gap-3 mb-4">
                                      <span className="assess-mod-paper-preview-section-badge bg-[#1e293b] text-white text-[9px] font-black uppercase tracking-widest rounded-full shadow-lg px-3 py-1">
                                        MCQ Questions
                                      </span>
                                      <span className="text-[7.5px] font-black uppercase tracking-wider text-rose-500 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0">
                                        Double-click to view details
                                      </span>
                                      <div className="flex-1 bg-slate-200" style={{ height: '1px' }}></div>
                                    </div>
                                    {mcqQs.map((q, idx) => renderQuestionRow(q, idx))}
                                  </>
                                )}
                                {descQs.length > 0 && (
                                  <>
                                    <div className="assess-mod-paper-preview-section-head flex items-center gap-3 mb-4">
                                      <span className="assess-mod-paper-preview-section-badge bg-[#1e293b] text-white text-[9px] font-black uppercase tracking-widest rounded-full shadow-lg px-3 py-1">
                                        Descriptive Questions
                                      </span>
                                      <div className="flex-1 bg-slate-200" style={{ height: '1px' }}></div>
                                    </div>
                                    {descQs.map((q, idx) => renderQuestionRow(q, mcqQs.length + idx))}
                                  </>
                                )}
                              </>
                            );
                          })()
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
          </div>
          {!isWizardMode && (
            <div className="assess-mod-qb-save-fixed flex items-center justify-end">
              <button
                type="button"
                onClick={handleSave}
                className="btn-assessment btn-assessment-danger flex items-center gap-2"
              >
                <FiFileText className="text-sm" />
                Save to Question Bank
              </button>
            </div>
          )}
        </main>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
        @keyframes dash {
          to { stroke-dashoffset: 0; }
        }
        .animate-dash {
          animation: dash 2s ease-in-out forwards;
        }
      `}</style>
      <AssessmentPopup 
        {...popupConfig}
        inputValue={popupInput}
        onInputChange={setPopupInput}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
      {viewingQuestion && (
        <AssessmentPopup
          isOpen={true}
          title="Question Details"
          message=""
          type="info"
          showCancel={false}
          confirmText="Close"
          containerClassName="!max-w-2xl w-full"
          showIcon={false}
          align="left"
          onConfirm={() => setViewingQuestion(null)}
          onCancel={() => setViewingQuestion(null)}
        >
          <div className="text-left mt-2 space-y-4 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar w-full">
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.type}
              </span>
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.pedagogy || 'General'}
              </span>
              <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">
                {viewingQuestion.marks} Marks
              </span>
            </div>

            <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Question</h4>
              <p className="text-sm font-bold text-[#1e293b] whitespace-pre-wrap leading-relaxed">
                {viewingQuestion.question.replace(/\s*\(Q\d+\)$/i, '')}
              </p>
            </div>

            {isMcqQuestionType(viewingQuestion.type) ? (
              <div className="space-y-2">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Options</h4>
                <div className="grid grid-cols-1 gap-2">
                  {['A', 'B', 'C', 'D'].map((letter) => {
                    const isCorrect = isMcqCorrectOption(viewingQuestion, letter);
                    return (
                      <div
                        key={letter}
                        className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-xs font-bold leading-relaxed ${
                          isCorrect
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'bg-slate-50 border-slate-100 text-slate-600'
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-black shrink-0 ${
                            isCorrect ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200'
                          }`}
                        >
                          {letter}
                        </span>
                        <span className="break-words text-left">{getMcqOptionText(viewingQuestion, letter)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              (viewingQuestion.correct_answer || viewingQuestion.expected_answer) && (
                <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Expected Answer</h4>
                  <p className="text-sm font-bold text-slate-600 whitespace-pre-wrap leading-relaxed text-left">
                    {viewingQuestion.correct_answer || viewingQuestion.expected_answer}
                  </p>
                </div>
              )
            )}
          </div>
        </AssessmentPopup>
      )}
    </div>
  );
};

export default QuestionBankManager;
